import { useEffect, useMemo, useState, useCallback } from "react";
import api, { fmtErr } from "@/shared/lib/api";
import Logo from "@/shared/components/Logo";
import { fmtIST } from "@/shared/lib/time";
import { STD_MATERIALS, STD_THICKNESS, STD_COLOURS } from "@/shared/lib/materialOptions";
import {
  Truck, LogOut, Ruler, ImageIcon, CheckCircle2, Clock, Package, FileText,
  Download, AlertTriangle, PlayCircle, Paperclip, CalendarClock, MapPin,
  ChevronRight, ChevronDown, ArrowLeft, FolderOpen, Layers, Pencil, Plus,
  Trash2, X, ShoppingCart, ListChecks, FileSpreadsheet,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/shared/lib/auth";

/* ────────────────────────────────────────────────────────────────────────────
   Procurement Portal — line-manner, sub-scope wise view.
   One glance = what to procure for what. Editable material list (edits are
   instantly reflected in the Excel download, which is built live from the DB).
   Shopping List tab groups common/standard items with totals and lists
   non-standard specials with their sources. Privacy-safe as before.
   ──────────────────────────────────────────────────────────────────────── */

const STATUS = {
  submitted: { label: "Pending", cls: "bg-amber-100 text-amber-800 border-amber-300", icon: Clock },
  in_progress: { label: "In Progress", cls: "bg-violet-100 text-violet-700 border-violet-300", icon: PlayCircle },
  procured: { label: "Procured", cls: "bg-emerald-100 text-emerald-700 border-emerald-300", icon: CheckCircle2 },
};

export default function ProcurementPortal() {
  const { user, logout } = useAuth();
  const [items, setItems] = useState(null);
  const [stats, setStats] = useState({});
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    Promise.all([api.get("/procurement/queue"), api.get("/procurement/stats")])
      .then(([q, s]) => { setItems(q.data.items || []); setStats(s.data || {}); })
      .catch((e) => { toast.error(fmtErr(e.response?.data?.detail) || "Failed to load"); setItems([]); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const projects = useMemo(() => {
    const map = {};
    for (const it of items || []) (map[it.project_code] ||= []).push(it);
    return Object.entries(map).map(([code, lines]) => {
      const reqs = lines.map((l) => l.required_by).filter(Boolean).sort();
      return {
        code, lines,
        lineCount: lines.length,
        materials: lines.reduce((s, l) => s + (l.items?.length || 0), 0),
        pending: lines.filter((l) => l.status === "submitted").length,
        in_progress: lines.filter((l) => l.status === "in_progress").length,
        procured: lines.filter((l) => l.status === "procured").length,
        delayed: lines.filter((l) => l.delayed).length,
        earliest: reqs[0] || "",
      };
    }).sort((a, b) =>
      (b.delayed - a.delayed) || (b.pending - a.pending) || a.code.localeCompare(b.code));
  }, [items]);

  const activeProject = selected ? projects.find((p) => p.code === selected) : null;

  return (
    <div className="min-h-screen bg-stone-50 text-zinc-900" data-testid="procurement-portal">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={30} tagline={false} />
            <div className="hidden sm:flex items-center gap-2 pl-3 border-l border-zinc-200">
              <Truck className="w-4 h-4 text-amber-600" />
              <span className="font-display font-bold tracking-tight">Procurement</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-zinc-500">{user?.name}</span>
            <button onClick={logout} data-testid="procurement-logout"
                    className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-rose-600 transition-colors">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-8 sm:py-10">
        {items === null ? (
          <div className="py-20 text-center text-zinc-400">Loading…</div>
        ) : activeProject ? (
          <ProjectDetail project={activeProject} onBack={() => setSelected(null)} onDone={load} />
        ) : (
          <ProjectList projects={projects} stats={stats} onOpen={setSelected} />
        )}
      </main>
    </div>
  );
}

/* ── PROJECT LIST ──────────────────────────────────────────────────────── */
function ProjectList({ projects, stats, onOpen }) {
  return (
    <>
      <div className="mb-7">
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber-600">Material to procure</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Projects</h1>
        <p className="text-zinc-500 mt-2 max-w-2xl text-sm">
          Open a project for a sub-scope wise list of everything to procure — or download the Excel.
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        <StatTile label="Projects" value={projects.length} tone="zinc" testid="stat-projects" />
        <StatTile label="Pending" value={stats.pending || 0} tone="amber" testid="stat-pending" />
        <StatTile label="In Progress" value={stats.in_progress || 0} tone="violet" testid="stat-inprogress" />
        <StatTile label="Delayed" value={stats.delayed || 0} tone="rose" testid="stat-delayed" />
      </div>

      {projects.length === 0 ? (
        <div className="py-20 text-center" data-testid="procurement-empty">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-amber-50 mb-4">
            <Package className="w-7 h-7 text-amber-400" />
          </div>
          <div className="font-display text-xl font-bold">Nothing to procure yet</div>
          <p className="text-zinc-500 mt-1 text-sm">When the design team submits material requirements, projects appear here.</p>
        </div>
      ) : (
        <div className="grid gap-3" data-testid="procurement-project-list">
          {projects.map((p) => (
            <button key={p.code} onClick={() => onOpen(p.code)} data-testid={`proc-project-${p.code}`}
                    className="group text-left bg-white rounded-2xl border border-zinc-200 shadow-sm hover:border-amber-300 hover:shadow transition p-5 sm:p-6 flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-amber-50 text-amber-600 shrink-0">
                <FolderOpen className="w-6 h-6" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display text-lg font-bold tracking-tight">{p.code}</span>
                  {p.delayed > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 h-6 rounded-full text-[10px] font-semibold border bg-rose-50 text-rose-700 border-rose-200">
                      <AlertTriangle className="w-3 h-3" /> {p.delayed} delayed
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-zinc-500">
                  <span className="inline-flex items-center gap-1"><Layers className="w-3.5 h-3.5 text-zinc-400" /> {p.lineCount} line item{p.lineCount !== 1 ? "s" : ""}</span>
                  <span className="inline-flex items-center gap-1"><Package className="w-3.5 h-3.5 text-zinc-400" /> {p.materials} material{p.materials !== 1 ? "s" : ""}</span>
                  {p.pending > 0 && <span className="text-amber-700 font-medium">{p.pending} pending</span>}
                  {p.in_progress > 0 && <span className="text-violet-700 font-medium">{p.in_progress} in progress</span>}
                  {p.procured > 0 && <span className="text-emerald-700 font-medium">{p.procured} procured</span>}
                  {p.earliest && <span className="inline-flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5 text-zinc-400" /> by {p.earliest}</span>}
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-zinc-300 group-hover:text-amber-500 transition shrink-0" />
            </button>
          ))}
        </div>
      )}
    </>
  );
}

/* ── PROJECT DETAIL: sub-scope wise line list + shopping list + excel ────── */
function ProjectDetail({ project, onBack, onDone }) {
  const [tab, setTab] = useState("lines");
  const [downloading, setDownloading] = useState(false);
  const [editing, setEditing] = useState(null); // line being edited

  async function downloadExcel() {
    setDownloading(true);
    try {
      const r = await api.get("/procurement/export.xlsx", { params: { code: project.code }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `procurement-${project.code}.xlsx`; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Excel downloaded — includes all edits.");
    } catch (e) { toast.error("Excel download failed"); }
    finally { setDownloading(false); }
  }

  // group lines by sub-scope
  const groups = useMemo(() => {
    const m = {}; const order = [];
    for (const it of project.lines) {
      const g = it.group || "General";
      if (!m[g]) { m[g] = []; order.push(g); }
      m[g].push(it);
    }
    return order.map((g) => [g, m[g]]);
  }, [project.lines]);

  return (
    <>
      <button onClick={onBack} data-testid="proc-back"
              className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 transition mb-4">
        <ArrowLeft className="w-4 h-4" /> All projects
      </button>

      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber-600">Procurement for project</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1" data-testid="proc-project-title">{project.code}</h1>
          <p className="text-zinc-500 mt-2 text-sm">
            {project.lineCount} line item{project.lineCount !== 1 ? "s" : ""} · {project.materials} material{project.materials !== 1 ? "s" : ""} to procure
            {project.delayed > 0 && <span className="text-rose-600 font-medium"> · {project.delayed} delayed</span>}
          </p>
        </div>
        <button onClick={downloadExcel} disabled={downloading} data-testid="proc-project-excel"
                className="inline-flex items-center gap-2 px-4 h-11 rounded-xl bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition disabled:opacity-50">
          <FileSpreadsheet className="w-4 h-4" /> {downloading ? "Preparing…" : "Download Excel"}
        </button>
      </div>

      {/* tabs */}
      <div className="inline-flex items-center gap-1 bg-white border border-zinc-200 rounded-xl p-1 mb-6" data-testid="proc-tabs">
        <TabBtn active={tab === "lines"} onClick={() => setTab("lines")} icon={ListChecks} label="Line Items" testid="proc-tab-lines" />
        <TabBtn active={tab === "shopping"} onClick={() => setTab("shopping")} icon={ShoppingCart} label="Shopping List" testid="proc-tab-shopping" />
      </div>

      {tab === "lines" ? (
        <div className="space-y-6" data-testid="proc-line-items">
          {groups.map(([g, lines]) => (
            <div key={g}>
              <div className="flex items-center gap-2 mb-2 px-1">
                <FolderOpen className="w-4 h-4 text-amber-600" />
                <span className="font-display font-bold text-zinc-900">{g}</span>
                <span className="text-xs text-zinc-400">· {lines.length} item{lines.length !== 1 ? "s" : ""}</span>
              </div>
              <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm divide-y divide-zinc-100 overflow-hidden">
                {lines.map((it) => (
                  <ProcRow key={it.boq_id} it={it} onDone={onDone} onEdit={() => setEditing(it)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <ShoppingList lines={project.lines} />
      )}

      {editing && (
        <ItemsEditor line={editing} onClose={() => setEditing(null)}
                     onSaved={() => { setEditing(null); onDone(); }} />
      )}
    </>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className={`inline-flex items-center gap-2 px-4 h-9 rounded-lg text-sm font-medium transition
              ${active ? "bg-zinc-900 text-white shadow" : "text-zinc-600 hover:bg-zinc-100"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

/* ── one line — compact row, expandable details ──────────────────────────── */
function ProcRow({ it, onDone, onEdit }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const st = STATUS[it.status] || STATUS.submitted;
  const StIcon = st.icon;
  const dim = (it.site.actual_height && it.site.actual_width)
    ? `${it.site.actual_height} × ${it.site.actual_width} ${it.site.actual_unit || ""}` : null;

  async function setStatus(status, e) {
    e.stopPropagation();
    setBusy(true);
    try {
      await api.patch(`/procurement/${it.project_code}/${it.boq_id}/status`, { status });
      toast.success(status === "procured" ? "Marked procured." : "Procurement started.");
      onDone();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail) || "Update failed"); }
    finally { setBusy(false); }
  }

  return (
    <div data-testid={`proc-card-${it.boq_id}`}>
      {/* compact row */}
      <div className="flex items-center gap-3 px-4 sm:px-5 py-3 cursor-pointer hover:bg-stone-50 transition"
           onClick={() => setOpen((o) => !o)} data-testid={`proc-row-${it.boq_id}`}>
        <ChevronDown className={`w-4 h-4 text-zinc-300 shrink-0 transition-transform ${open ? "" : "-rotate-90"}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-zinc-900 truncate">{it.wall_label || it.title}</span>
            {it.delayed && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-rose-600">
                <AlertTriangle className="w-3 h-3" /> OVERDUE
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mt-1">
            {(it.items || []).map((m, i) => (
              <span key={i}
                    className={`inline-flex items-center gap-1 px-2 h-6 rounded-full text-[11px] border
                      ${m.kind === "standard"
                        ? "bg-indigo-50 text-indigo-700 border-indigo-200"
                        : "bg-orange-50 text-orange-800 border-orange-300 font-medium"}`}>
                {m.kind === "standard" ? (m.material || "—") : (m.spec || "special")}
                {(m.thickness || m.colour) && <span className="text-[10px] opacity-70">{[m.thickness, m.colour].filter(Boolean).join("/")}</span>}
                <b>{m.quantity}{m.unit ? ` ${m.unit}` : ""}</b>
              </span>
            ))}
            {(it.items || []).length === 0 && <span className="text-xs text-zinc-400">No materials declared</span>}
          </div>
        </div>
        <span className={`hidden sm:inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full text-[11px] font-medium border shrink-0 ${st.cls}`}
              data-testid={`proc-status-${it.boq_id}`}>
          <StIcon className="w-3.5 h-3.5" /> {st.label}
        </span>
        <button onClick={(e) => { e.stopPropagation(); onEdit(); }} data-testid={`proc-edit-${it.boq_id}`}
                title="Edit material list"
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-zinc-400 hover:text-amber-700 hover:bg-amber-50 transition shrink-0">
          <Pencil className="w-4 h-4" />
        </button>
        {it.status === "submitted" && (
          <button onClick={(e) => setStatus("in_progress", e)} disabled={busy} data-testid={`proc-start-${it.boq_id}`}
                  className="hidden sm:inline-flex items-center gap-1.5 px-3 h-8 rounded-lg bg-violet-600 text-white text-xs font-medium hover:bg-violet-700 transition disabled:opacity-50 shrink-0">
            <PlayCircle className="w-3.5 h-3.5" /> Start
          </button>
        )}
        {(it.status === "submitted" || it.status === "in_progress") && (
          <button onClick={(e) => setStatus("procured", e)} disabled={busy} data-testid={`proc-procured-${it.boq_id}`}
                  className="hidden sm:inline-flex items-center gap-1.5 px-3 h-8 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 transition disabled:opacity-50 shrink-0">
            <CheckCircle2 className="w-3.5 h-3.5" /> Procured
          </button>
        )}
      </div>

      {/* expanded details */}
      {open && (
        <div className="px-11 pb-4 space-y-3" data-testid={`proc-details-${it.boq_id}`}>
          <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-xs">
            {it.required_by && (
              <span className={`inline-flex items-center gap-1.5 ${it.delayed ? "text-rose-600 font-semibold" : "text-zinc-600"}`}>
                <CalendarClock className="w-3.5 h-3.5" /> Required by {it.required_by}
                {it.days_left != null && (it.delayed ? ` · OVERDUE ${Math.abs(it.days_left)}d` : ` · ${it.days_left}d left`)}
              </span>
            )}
            {it.site.surface_type && (
              <span className="inline-flex items-center gap-1.5 text-zinc-600">
                <MapPin className="w-3.5 h-3.5 text-zinc-400" /> Surface: <b>{it.site.surface_type}</b>
              </span>
            )}
            {dim && (
              <span className="inline-flex items-center gap-1.5 text-emerald-700 font-semibold">
                <Ruler className="w-3.5 h-3.5" /> {dim}
              </span>
            )}
            {it.approved_design && (
              <a href={it.approved_design.url} target="_blank" rel="noreferrer"
                 onClick={(e) => e.stopPropagation()}
                 className="inline-flex items-center gap-1.5 text-amber-700 hover:underline" data-testid={`proc-design-${it.boq_id}`}>
                <FileText className="w-3.5 h-3.5" /> Approved design
              </a>
            )}
            {it.status === "procured" && it.procured_at && (
              <span className="inline-flex items-center gap-1.5 text-emerald-700">
                <CheckCircle2 className="w-3.5 h-3.5" /> Procured {fmtIST(it.procured_at)}
              </span>
            )}
          </div>
          {(it.items || []).some((m) => m.remarks || m.attachment_url) && (
            <div className="space-y-1">
              {(it.items || []).filter((m) => m.remarks || m.attachment_url).map((m, i) => (
                <div key={i} className="text-[11px] text-zinc-500 flex items-center gap-1.5">
                  <span className="font-medium text-zinc-600">{m.kind === "standard" ? m.material : m.spec}:</span>
                  {m.remarks}
                  {m.attachment_url && (
                    <a href={m.attachment_url} target="_blank" rel="noreferrer" className="text-amber-600 inline-flex items-center gap-0.5">
                      <Paperclip className="w-3 h-3" /> {m.attachment_name || "attachment"}
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
          {it.site.photos?.length > 0 && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-1.5 flex items-center gap-1">
                <ImageIcon className="w-3 h-3" /> Site reference
              </div>
              <div className="flex flex-wrap gap-2">
                {it.site.photos.map((p, i) => (
                  <a key={i} href={p} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                     className="block w-20 h-16 rounded-lg overflow-hidden border border-zinc-200 hover:ring-2 hover:ring-amber-300 transition">
                    <img src={p} alt="site" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                  </a>
                ))}
              </div>
            </div>
          )}
          <div className="flex sm:hidden gap-2">
            {it.status === "submitted" && (
              <button onClick={(e) => setStatus("in_progress", e)} disabled={busy}
                      className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-violet-600 text-white text-xs font-medium">
                <PlayCircle className="w-3.5 h-3.5" /> Start
              </button>
            )}
            {(it.status === "submitted" || it.status === "in_progress") && (
              <button onClick={(e) => setStatus("procured", e)} disabled={busy}
                      className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-emerald-600 text-white text-xs font-medium">
                <CheckCircle2 className="w-3.5 h-3.5" /> Procured
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── SHOPPING LIST: standard aggregated + non-standard specials ──────────── */
function ShoppingList({ lines }) {
  const { std, non } = useMemo(() => {
    const agg = {};
    const non = [];
    for (const it of lines) {
      for (const m of it.items || []) {
        if (m.kind === "standard") {
          const k = [m.material || "", m.thickness || "", m.colour || "", m.unit || ""].join("|");
          const e = (agg[k] ||= { material: m.material, thickness: m.thickness, colour: m.colour, unit: m.unit, qty: 0, walls: new Set() });
          e.qty += Number(m.quantity) || 0;
          e.walls.add(it.wall_label || it.title);
        } else {
          non.push({ ...m, wall: it.wall_label || it.title });
        }
      }
    }
    return { std: Object.values(agg).sort((a, b) => (a.material || "").localeCompare(b.material || "")), non };
  }, [lines]);

  return (
    <div className="grid gap-6" data-testid="proc-shopping-list">
      <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 bg-indigo-50/60 border-b border-indigo-100 flex items-center gap-2">
          <Package className="w-4 h-4 text-indigo-600" />
          <span className="font-display font-bold text-zinc-900">Standard items</span>
          <span className="text-xs text-zinc-400">· common materials, totalled across walls</span>
        </div>
        {std.length === 0 ? (
          <div className="px-5 py-8 text-sm text-zinc-400 text-center">No standard items.</div>
        ) : (
          <div className="divide-y divide-zinc-100">
            <div className="hidden sm:grid grid-cols-[1fr_90px_100px_110px_1fr] gap-3 px-5 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-400">
              <span>Material</span><span>Thickness</span><span>Colour</span><span>Total qty</span><span>Used in</span>
            </div>
            {std.map((s, i) => (
              <div key={i} className="grid sm:grid-cols-[1fr_90px_100px_110px_1fr] gap-x-3 gap-y-1 px-5 py-3 text-sm items-center"
                   data-testid={`shopping-std-${i}`}>
                <span className="font-semibold text-zinc-900">{s.material || "—"}</span>
                <span className="text-zinc-600">{s.thickness || "—"}</span>
                <span className="text-zinc-600">{s.colour || "—"}</span>
                <span className="font-mono font-bold text-indigo-700">{Math.round(s.qty * 100) / 100} {s.unit}</span>
                <span className="text-xs text-zinc-400 truncate">{[...s.walls].join(", ")}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 bg-orange-50/70 border-b border-orange-100 flex items-center gap-2">
          <ShoppingCart className="w-4 h-4 text-orange-600" />
          <span className="font-display font-bold text-zinc-900">Non-standard items</span>
          <span className="text-xs text-zinc-400">· specials with their procurement source</span>
        </div>
        {non.length === 0 ? (
          <div className="px-5 py-8 text-sm text-zinc-400 text-center">No non-standard items.</div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {non.map((m, i) => (
              <div key={i} className="px-5 py-3 text-sm" data-testid={`shopping-nonstd-${i}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-zinc-900">{m.spec || "—"}</span>
                  <span className="font-mono font-bold text-orange-700">{m.quantity} {m.unit}</span>
                  {m.source && <span className="text-xs text-orange-800 bg-orange-50 border border-orange-200 rounded-full px-2 py-0.5">from {m.source}</span>}
                  <span className="ml-auto text-xs text-zinc-400">{m.wall}</span>
                </div>
                {m.remarks && <div className="text-[11px] text-zinc-400 mt-1">↳ {m.remarks}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── EDITOR: procurement team edits the material list ────────────────────── */
function ItemsEditor({ line, onClose, onSaved }) {
  const [rows, setRows] = useState(() =>
    (line.items || []).map((m) => ({
      kind: m.kind || "standard", material: m.material || "", thickness: m.thickness || "",
      colour: m.colour || "", spec: m.spec || "", source: m.source || "",
      quantity: m.quantity || 0, unit: m.unit || "Nos", remarks: m.remarks || "",
      attachment_url: m.attachment_url || "", attachment_name: m.attachment_name || "",
    })));
  const [saving, setSaving] = useState(false);

  const set = (i, k, v) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const addRow = (kind) => setRows((r) => [...r, { kind, material: "", thickness: "", colour: "", spec: "", source: "", quantity: 1, unit: "Nos", remarks: "" }]);
  const delRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));

  async function save() {
    for (const r of rows) {
      if (r.kind === "standard" && !r.material) { toast.error("Every standard item needs a material."); return; }
      if (r.kind === "non_standard" && !(r.spec || "").trim()) { toast.error("Every non-standard item needs a specification."); return; }
    }
    setSaving(true);
    try {
      await api.put(`/procurement/${line.project_code}/${line.boq_id}/items`, { items: rows });
      toast.success("Material list updated — the Excel will reflect this.");
      onSaved();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="proc-editor-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-amber-600">Edit material list</div>
            <div className="font-display text-lg font-bold">{line.wall_label || line.title}</div>
          </div>
          <button onClick={onClose} data-testid="proc-editor-close" className="text-zinc-400 hover:text-zinc-700"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-5 py-4 overflow-y-auto space-y-3 flex-1">
          {rows.map((r, i) => (
            <div key={i} className={`rounded-xl border p-3 space-y-2 ${r.kind === "standard" ? "border-indigo-200 bg-indigo-50/40" : "border-orange-200 bg-orange-50/40"}`}
                 data-testid={`proc-editor-row-${i}`}>
              <div className="flex items-center gap-2">
                <select value={r.kind} onChange={(e) => set(i, "kind", e.target.value)}
                        className="text-xs font-medium rounded-lg border border-zinc-300 px-2 h-8 bg-white"
                        data-testid={`proc-editor-kind-${i}`}>
                  <option value="standard">Standard</option>
                  <option value="non_standard">Non-standard</option>
                </select>
                <span className="text-[10px] text-zinc-400">{r.kind === "standard" ? "pick from the standard catalogue" : "special item — spec + source"}</span>
                <button onClick={() => delRow(i)} data-testid={`proc-editor-del-${i}`}
                        className="ml-auto text-zinc-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
              </div>
              {r.kind === "standard" ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <select value={r.material} onChange={(e) => set(i, "material", e.target.value)}
                          className="text-sm rounded-lg border border-zinc-300 px-2 h-9 bg-white" data-testid={`proc-editor-material-${i}`}>
                    <option value="">Material…</option>
                    {STD_MATERIALS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <select value={r.thickness} onChange={(e) => set(i, "thickness", e.target.value)}
                          className="text-sm rounded-lg border border-zinc-300 px-2 h-9 bg-white">
                    <option value="">Thickness…</option>
                    {STD_THICKNESS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <select value={r.colour} onChange={(e) => set(i, "colour", e.target.value)}
                          className="text-sm rounded-lg border border-zinc-300 px-2 h-9 bg-white">
                    <option value="">Colour…</option>
                    {STD_COLOURS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-2">
                  <input value={r.spec} onChange={(e) => set(i, "spec", e.target.value)} placeholder="Specification (what exactly)"
                         className="text-sm rounded-lg border border-zinc-300 px-3 h-9" data-testid={`proc-editor-spec-${i}`} />
                  <input value={r.source} onChange={(e) => set(i, "source", e.target.value)} placeholder="Procure from (source)"
                         className="text-sm rounded-lg border border-zinc-300 px-3 h-9" data-testid={`proc-editor-source-${i}`} />
                </div>
              )}
              <div className="flex gap-2">
                <input type="number" min="0" step="any" value={r.quantity}
                       onChange={(e) => set(i, "quantity", e.target.value === "" ? 0 : Number(e.target.value))}
                       className="text-sm rounded-lg border border-zinc-300 px-3 h-9 w-24" data-testid={`proc-editor-qty-${i}`} />
                <select value={r.unit} onChange={(e) => set(i, "unit", e.target.value)}
                        className="text-sm rounded-lg border border-zinc-300 px-2 h-9 bg-white w-24">
                  {["Nos", "Sq ft", "Sq m", "Ft", "M", "Sheets", "Rolls", "Kg", "Litre", "Set"].map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
                <input value={r.remarks} onChange={(e) => set(i, "remarks", e.target.value)} placeholder="Remarks (optional)"
                       className="text-sm rounded-lg border border-zinc-300 px-3 h-9 flex-1" />
              </div>
            </div>
          ))}
          <div className="flex gap-2">
            <button onClick={() => addRow("standard")} data-testid="proc-editor-add-std"
                    className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-indigo-300 text-indigo-700 text-xs font-medium hover:bg-indigo-50">
              <Plus className="w-3.5 h-3.5" /> Standard item
            </button>
            <button onClick={() => addRow("non_standard")} data-testid="proc-editor-add-nonstd"
                    className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-orange-300 text-orange-700 text-xs font-medium hover:bg-orange-50">
              <Plus className="w-3.5 h-3.5" /> Non-standard item
            </button>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-zinc-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 h-10 rounded-xl text-sm text-zinc-600 hover:bg-zinc-100">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="proc-editor-save"
                  className="inline-flex items-center gap-2 px-5 h-10 rounded-xl bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 disabled:opacity-50">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatTile({ label, value, tone, testid }) {
  const tones = {
    zinc: "text-zinc-900", amber: "text-amber-600", violet: "text-violet-600",
    emerald: "text-emerald-600", rose: "text-rose-600",
  };
  return (
    <div data-testid={testid} className="rounded-2xl border border-zinc-200 bg-white p-4">
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 flex items-center gap-1">
        {tone === "rose" && value > 0 && <AlertTriangle className="w-3 h-3 text-rose-500" />}{label}
      </div>
      <div className={`font-display text-3xl font-bold mt-1 ${tones[tone]}`}>{value}</div>
    </div>
  );
}
